import { Button, Checkbox, DatePicker, message, Row, Table, Typography, Card } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { serverFetch } from "../../server";
import { fmtDate, strDate2Server } from "../../extensions/utils";
import { renderCheckbox, storeDocVidName, storeDocVids } from "../../extensions/helper";
import StockDoc from "./stock-doc";
import { AddFilter, FilterItem, SportTooltip } from "../../controls";
import StockOrder from "./stock-order";
import ButtonReport from "../../controls/button-report/button-report";
import TriStateCheckbox from "../../controls/tri-state-checkbox";
import { HelpButton } from "../../extensions/design";
import { isMobile, menuSize } from "../../extensions/design";
import { useLocalStorageFilter, applyDateRoll } from "@/extensions/use-local-storage-filter";
const { Text } = Typography;

const StockDocs = ({ filter: externalFilter, onItemsChange, onOptionsChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allClientOutOptions, setAllClientOutOptions] = useState([]);
  const [allClientInOptions, setAllClientInOptions] = useState([]);
  const allClientOutOptionsRef = useRef([]);
  const allClientInOptionsRef = useRef([]);
  // Инициализация фильтра из localStorage или значения по умолчанию
  const [filter, setFilter] = useLocalStorageFilter(
    'stockDocsFilter',
    () => ({
      dateFrom: dayjs().subtract(1, "month"),
      dateTo: dayjs(),
      Active: null,
    }),
    (parsed) => {
      const { dateFrom, dateTo } = applyDateRoll(
        parsed.dateTo ? dayjs(parsed.dateTo) : null,
        dayjs().subtract(1, "month"),
        dayjs(),
        parsed.dateFrom ? dayjs(parsed.dateFrom) : null,
      );
      const getIdsValue = (val) => {
        if (!val) return null;
        if (Array.isArray(val)) return val;
        return val.value ?? null;
      };
      return {
        dateFrom: typeof dateFrom === 'string' ? dayjs(dateFrom) : dateFrom,
        dateTo: typeof dateTo === 'string' ? dayjs(dateTo) : dateTo,
        Active: parsed.Active ?? null,
        ...(getIdsValue(parsed.clientOutIds)?.length ? { clientOutIds: { value: getIdsValue(parsed.clientOutIds), inputType: "multiselect" } } : {}),
        ...(getIdsValue(parsed.clientInIds)?.length ? { clientInIds: { value: getIdsValue(parsed.clientInIds), inputType: "multiselect" } } : {}),
        ...(getIdsValue(parsed.documentVidIds)?.length ? { documentVidIds: { value: getIdsValue(parsed.documentVidIds), inputType: "multiselect" } } : {}),
      };
    },
    (filter) => ({
      dateFrom: filter.dateFrom?.format('YYYY-MM-DD'),
      dateTo: filter.dateTo?.format('YYYY-MM-DD'),
      Active: filter.Active,
      ...(filter.clientOutIds?.value != null ? { clientOutIds: { value: filter.clientOutIds.value } } : {}),
      ...(filter.clientInIds?.value != null ? { clientInIds: { value: filter.clientInIds.value } } : {}),
      ...(filter.documentVidIds?.value != null ? { documentVidIds: { value: filter.documentVidIds.value } } : {}),
    }),
  );

  const filterRef = useRef(filter);
  useEffect(() => { filterRef.current = filter; }, [filter]);

  const [unfilteredLoaded, setUnfilteredLoaded] = useState(false);

  useEffect(() => {
    const saved = filterRef.current || {};
    getData({
      dateFrom: saved.dateFrom || dayjs().subtract(1, "month"),
      dateTo: saved.dateTo || dayjs(),
      Active: saved.Active ?? null,
    }, 'mount-unfiltered').then(() => {
      setUnfilteredLoaded(true);
    });
  }, []);

  useEffect(() => {
    const outMap = new Map(allClientOutOptionsRef.current.map(o => [o.value, o]));
    const inMap = new Map(allClientInOptionsRef.current.map(o => [o.value, o]));
    items.forEach(item => {
      if (item.clientOutId && item.clientOutName) {
        outMap.set(item.clientOutId, { value: item.clientOutId, label: item.clientOutName });
      }
      if (item.clientInId && item.clientInName) {
        inMap.set(item.clientInId, { value: item.clientInId, label: item.clientInName });
      }
    });
    const currentFilter = filterRef.current;
    if (currentFilter?.clientOutIds?.options?.length) {
      currentFilter.clientOutIds.options.forEach(opt => outMap.set(opt.value, opt));
    }
    if (currentFilter?.clientInIds?.options?.length) {
      currentFilter.clientInIds.options.forEach(opt => inMap.set(opt.value, opt));
    }
    allClientOutOptionsRef.current = Array.from(outMap.values());
    allClientInOptionsRef.current = Array.from(inMap.values());
    setAllClientOutOptions(allClientOutOptionsRef.current);
    setAllClientInOptions(allClientInOptionsRef.current);
    onOptionsChange?.(allClientOutOptionsRef.current, allClientInOptionsRef.current);
  }, [items]);

  const isFirstRender = useRef(true);
  const filterSourceRef = useRef('internal');

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (filterSourceRef.current === 'internal' && unfilteredLoaded && (!isMobile || externalFilter === null)) {
      getData(filter, 'filter-change');
    }
    filterSourceRef.current = 'internal';
  }, [filter]);

  useEffect(() => {
    if (externalFilter !== null && unfilteredLoaded) {
      filterSourceRef.current = 'external';
      const allowedKeys = ['clientOutIds', 'clientInIds', 'documentVidIds', 'Active'];
      const rawFilter = allowedKeys.reduce((acc, key) => {
        if (externalFilter[key] !== undefined) {
          acc[key] = externalFilter[key];
        }
        return acc;
      }, {});
      const nextFilter = { dateFrom: filter.dateFrom, dateTo: filter.dateTo, ...rawFilter };
      setFilter(nextFilter);
      getData(nextFilter, 'external-apply');
    }
  }, [externalFilter, unfilteredLoaded]);
  
  const divRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [newOrder, setNewOrder] = useState(false);
  const [addingFilter, setAddingFilter] = useState(true);


  const filterOptions = [
    { type: "clientOutIds", label: "Отправитель", tooltip: "Отправитель", inputType: "multiselect", options: allClientOutOptions },
    { type: "clientInIds", label: "Получатель", tooltip: "Получатель", inputType: "multiselect", options: allClientInOptions },
    { type: "documentVidIds", label: "Вид документа", tooltip: "По виду документа", inputType: "multiselect", options: storeDocVids, width: 250 },
  ];

  const filterLabels = {
    clientOutIds: "Отправитель",
    clientInIds: "Получатель",
    documentVidIds: "Вид документа",
  };

  const renderFilterItem = (key, data) => {
    const option = filterOptions.find(f => f.type === key);
    return (
      <FilterItem key={key} type={option?.inputType} label={filterLabels[key] || key} value={data.value} options={option?.options}
        onChange={(val) => onFilterChange({ [key]: val })}
        onRemove={() => {
          const { [key]: _, ...rest } = filter;
          setFilter(rest);
        }}
      />
    );
  };
  const filters = Object.entries(filter)
    .filter(([key, data]) => !["dateFrom", "dateTo", "Active"].includes(key))
    .map(([type, data]) => ({ type, ...data }));

  const buildParams = useCallback((filter) => {
    const params = new URLSearchParams();
    /* params.append("Offset", offset);
    params.append("Limit", PAGE_SIZE); */
    /* if (searchValue) params.append("SearchText", searchValue); */
    if (filter.clientOutIds?.value?.length) filter.clientOutIds.value.forEach(v => params.append("clientOutIds", v));
    if (filter.clientInIds?.value?.length) filter.clientInIds.value.forEach(v => params.append("clientInIds", v));
    if (filter.documentVidIds?.value?.length) filter.documentVidIds.value.forEach(v => params.append("documentVidIds", v));
    if (filter.Active != null) params.append("Active", filter.Active);
    
    return params;

  }, [filter]);

  const getData = (f = filter) => {
    setLoading(true);
    return serverFetch(`store?dateFrom=${strDate2Server(f.dateFrom)}&dateTo=${strDate2Server(f.dateTo)}&${buildParams(f)}`)
      .then(data => {
        setLoading(false);
        setItems(data);
        onItemsChange?.(data);
        return data;
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }
  
  const handleAddTopFilter = (type) => {
    if (filter[type]) return;

    const option = filterOptions.find(opt => opt.type === type);
    const inputType = option?.inputType;
    let initialValue = undefined;
    let options = option?.options;
    onFilterChange({ [type]: { value: initialValue, inputType, options }});
    setAddingFilter(false);
  };

  const onFilterChange = (changedValues) => {
    const newFilter = { ...filter };
    Object.keys(changedValues).forEach(key => {
      const val = changedValues[key];
      if (Array.isArray(val)) {
        newFilter[key] = { value: val, inputType: "multiselect" };
      } else {
        newFilter[key] = val;
      }
    });
    setFilter(newFilter);
    if (isMobile) {
      getData(newFilter);
    }
  };

  const columns = [
    { dataIndex: "id",title: "#", },
    { dataIndex: "docDate", render: fmtDate, title: "Дата", },
    { dataIndex: "active", render: renderCheckbox, title: "Акт.", },
    { dataIndex: "vid", title: "Вид", render: storeDocVidName },
    { dataIndex: "clientOutName", title: "Отправитель", },
    { dataIndex: "clientInName", title: "Получатель", },
    { dataIndex: "comment", title: "Комментарий", },
    { dataIndex: "syma",title: "Сумма", align: "right"},
  ]

  const headerSize =  divRef.current?.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
  const tableHeight = divRef.current ? (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY) : 0;
  const tableWidth = (document.documentElement.offsetWidth - divRef.current?.getBoundingClientRect().left) || 0;

  const onAdd = () => {
    setSelected(0);
  }

  const onClose = refresh => {
    setSelected(null);
    refresh && getData(filter);
  }

  const onDocDataChanged = () => {
    getData(filter);
  }

  const onCloseOrder = refresh => {
    setNewOrder(false);
    refresh && getData(filter);
  }

  return !isMobile ? (<div>
    <div className="sport-form-title">
        <div>Период с</div>
        <DatePicker value={filter.dateFrom} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateFrom: e })} allowClear={false}/>
        <div>по</div>
        <DatePicker value={filter.dateTo} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateTo: e })} allowClear={false}/>
        <Button onClick={() => getData(filter)}>Обновить</Button>
        <Button onClick={onAdd}>Новый</Button>
        <SportTooltip title="Сформировать заказ поставщику для товаров с недостаточными остатками">
          <Button onClick={() => setNewOrder(true)}>Заказ поставщику</Button>
        </SportTooltip>
        <ButtonReport id={selected} vid={5} begPer={filter.dateFrom} endPer={filter.dateTo} />
        <TriStateCheckbox name="Active" value={filter.Active} onChange={(val) => onFilterChange({ Active: val })}>Активный</TriStateCheckbox>
        <HelpButton article="uchet-nakladnyh"/>
      </div>
    <div ref={divRef} style={{width: tableWidth}}>
      <Row style={{ marginLeft: "8px", marginBottom:"8px" }}>
        <div className="filter-container">
          {filters.length === 0 && !addingFilter && (
            <AddFilter availableFilters={filterOptions} usedFilters={filters.map(f => f.type)} onAdd={handleAddTopFilter}/>
          )}
          {filters.map((f) => renderFilterItem(f.type, f))}
          {filters.length < filterOptions.length && (
            addingFilter ? (
              <AddFilter availableFilters={filterOptions} usedFilters={filters.map(f => f.type)} onAdd={handleAddTopFilter}/>
            ) : (
              filters.length !== 0 && (
                <Button onClick={() => setAddingFilter(true)} type="dashed" className="filter-add-button">+</Button>
              )
            )
          )}
        </div>
      </Row>  
    <Table dataSource={items} loading={loading} columns={columns} pagination={false} rowKey="id" size="small" scroll={{ y: tableHeight }}
      rowSelection={{ type: 'radio', selectedRowKeys: [selected], renderCell: checked => <div className={checked ? "list-indicator" : ""} /> }}
      onRow={(record) => ({onClick: () => {setSelected(record.id)}})}
    />
    </div>
    {selected !== null && <StockDoc id={selected} onClose={onClose} onDocDataChanged={onDocDataChanged} />}
    {newOrder && <StockOrder onClose={onCloseOrder}/>}
  </div>) : (
    <>
      <div style={{display:"flex", justifyContent: "space-around", alignItems:"center"}}>
        <Text>Период с </Text>
        <DatePicker className="record-date-picker-mobile" value={filter.dateFrom} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateFrom: e })} allowClear={false}/>
        <Text> по </Text>
        <DatePicker className="record-date-picker-mobile" value={filter.dateTo} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateTo: e })} allowClear={false}/>
        <ButtonReport id={selected} vid={5} begPer={filter.dateFrom} endPer={filter.dateTo} iconOnly={true}/>
      </div>
      <div className="client-item-line-double-halfed" style={{margin: "4px"}}>
        <Button onClick={onAdd}>Новый</Button>
        <Button onClick={() => setNewOrder(true)}>Заказ поставщику</Button>
      </div>
      <div className="stock-scrollable-list" style={{ height: window.innerHeight - menuSize - 100}}>
        {items.map(item => {
          return (
          <Card key={item.id} className="stock-card" style={{ borderLeft: `4px solid ${item?.active ? "#2f6f00" : "#d9d9d9"}` }} onClick={() => {setSelected(item.id)}}>
            <div className="stock-osv-item-line-triple-non-even">
              <Text strong> {fmtDate(item.docDate)} </Text>
              <Text> {storeDocVidName(item.vid)} </Text>
              <Text style={{textAlign: "right"}}strong> #{item.id} </Text>
            </div>
            <div className="stock-osv-item-line-long-and-short">
              <Text className="sender"> Отправ.: {item.clientOutName} </Text>
              <Text style={{textAlign: "right"}}> Сумма: {item.syma} </Text>
            </div>
            <div className="stock-osv-item-line-long-and-short">
              <Text className="sender" style={{textAlign: "left"}}> Получ.: {item.clientInName} </Text>
              <div style={{textAlign: "right", fontSize: "12px"}}>
                <TriStateCheckbox disabled={true} name="Active" value={item.active} onChange={() => {}}>
                  <label style={{fontSize: "12px"}}>Активный</label>
                </TriStateCheckbox>
              </div>
            </div>
            <div>
              <Text>Комм.: {item.comment}</Text>
            </div>
          </Card>
        )})}
        {(items.length === 0 && !loading) ?
        <div style={{ textAlign: "center", marginTop: "20px", fontSize:"14px" }}>Нет данных
        </div> : <></>}
      </div>
      <></>
      {newOrder && <StockOrder onClose={onCloseOrder}/>}
      {selected !== null && <StockDoc id={selected} onClose={onClose} onDocDataChanged={onDocDataChanged} />}
    </>
  )
}

export default StockDocs;